import moment from 'moment'
import { sortByKey } from './array'

/**
 * Groups detections together based on how far apart they are from each other.
 *
 * @export
 * @class DetectionMerger
 */
export class DetectionMerger {
  /**
   *Creates an instance of DetectionMerger.
   * @param {object} options Options to configure the merger.
   * @memberof DetectionMerger
   */
  constructor (options = {}) {
    this._reset()
    this._options = Object.assign({}, DetectionMerger._defaultOptions, options)
  }

  /**
   *  Returns a default set of options to control the merging.
   *
   * Options:
   *  - secondsBetweenUnmergedDetections {number} If a detection happens N seconds after the previous one, it is merged.
   * - groupByFace: {boolean=false} If `true` the merger will check for a matching faceUUID
   * - keys {object} The keys to query for the face/timestamp/camera.
   *     -- cameraDescription {string=camera_description} Specify this if the detections describe the camera with a different key.
   *     -- faceUUID {string=face_uuid} Specify if the detections describe the person with a different key.
   *     -- timestamp {string=captured_at} Specify if the detections describe the timestamp with a different key.
   *
   * @static
   * @returns The default options for the merger.
   * @memberof DetectionMerger
   */
  static get _defaultOptions () {
    return {
      secondsBetweenUnmergedDetections: 300,
      // If this is `true`, the merger will compare the `faceUUID` property of every detection, and require them to match for the detections to be merged.
      groupByFace: false,
      keys: {
        cameraDescription: 'camera_description',
        faceUUID: 'frontend_face_uuid',
        timestamp: 'captured_at',
      },
    }
  }

  /**
   * Resets the merger.
   *
   * @memberof DetectionMerger
   */
  _reset () {
    this.mergedDetections = []
    this.currentByCamera = {}
  }

  /**
   * Merges detections that occur close together.
   *
   * @param {array} detections An array of detections to merge together.
   * @returns An array of detections, some of which will contain child detections.
   * @memberof DetectionMerger
   */
  groupedDetectionsFromDetections (detections) {
    this._reset()
    const byPerson = this._options.groupByFace
    const detectionsToGroup = byPerson
      ? this._detectionsDividedByFace(detections)
      : detections

    if (byPerson) {
      let arrayByPerson = Object.values(detectionsToGroup)

      const merger = this._temporaryMerger()
      const topLevelAlerts = arrayByPerson.map(detectionsPerPerson => {
        const sortedDetections = sortByKey(detectionsPerPerson, 'unixTimestamp')
        const groupedDetectionsPerPerson = merger.groupedDetectionsFromDetections(
          sortedDetections
        )
        return groupedDetectionsPerPerson[0]
      })
      const topLevelAlertsSorted = sortByKey(topLevelAlerts, 'unixTimestamp')
      return topLevelAlertsSorted
    }

    const grouped = this._groupEvents(detectionsToGroup)
    // console.log(`Inp len ${detections.length}\n out len ${grouped.length}`)
    return grouped
  }

  /**
   *  Creates a detection merger that doesn't group by face and returns it.
   *
   * @returns A detection merger.
   * @memberof DetectionMerger
   */
  _temporaryMerger () {
    const options = Object.assign(this._options, { groupByFace: false })
    const temporaryMerger = new DetectionMerger(options)
    return temporaryMerger
  }
  /**
   * Private method which groups detections together.
   *
   * @param {array} detections An array of detections.
   * @returns An array of detections where the detections may be merged, depending on the options supplied.
   * @memberof DetectionMerger
   */
  _groupEvents (detections) {
    detections.forEach(detection => {
      const previousParent = this._previousParent(detection)

      if (!previousParent) {
        this.addParentDetectionToList(detection)
      } else {
        const detectionToCompareTo = this.detectionToCompareTo(previousParent)

        if (this.shouldGroupDetections(detectionToCompareTo, detection)) {
          this.addChildDetectionToParent(detection, previousParent)
        } else {
          this.addParentDetectionToList(detection)
        }
      }
    })
    this.mergedDetections = this.mergedDetections.map(det => {
      if (!det.merged) {
        return det
      }
      const bestMatch = det.merged.reduce((prev, cur) => {
        if (cur.match_confidence > prev.match_confidence) {
          return cur
        }
        return prev
      }, det)
      return { ...det, bestMatch }
    })
    return this.mergedDetections
  }

  /**
   * Divide the detections into arrays by person.
   *
   * @param {array<object>} detections An array of detection objects.
   * @memberof DetectionMerger
   */
  _detectionsDividedByFace (detections) {
    const key = this._options.keys.faceUUID
    if (!key) {
      console.log(`Can't divide by face id without a key or a default key.`)
      return detections
    }

    let detectionsByFaceUUID = detections.reduce(
      (output, currentObject, currentIndex, input) => {
        const faceUUID = currentObject[key]

        if (!output[faceUUID]) {
          output[faceUUID] = [currentObject]
        } else {
          output[faceUUID].push(currentObject)
        }
        return output
      },
      {}
    )

    return detectionsByFaceUUID
  }

  /**
   * Check if two detections occurred within some number of seconds of each other.
   *
   * @param {any} detectionA The first detection to check.
   * @param {any} detectionB The second detection to check.
   * @returns true if either detection occurred less than
   */
  shouldGroupDetections (detectionA, detectionB) {
    const timeA = moment(detectionA[this._options.keys.timestamp]).valueOf()
    const timeB = moment(detectionB[this._options.keys.timestamp]).valueOf()

    const distanceBetweenTimes = Math.abs(timeA - timeB)
    const MILLISECONDS_PER_SECOND = 1000
    const threshold =
      this._options.secondsBetweenUnmergedDetections * MILLISECONDS_PER_SECOND
    const areProximate = distanceBetweenTimes <= threshold
    let isTheSamePerson = true
    if (this.groupByFace) {
      isTheSamePerson =
        detectionA[this._options.keys.faceUUID] ===
        detectionB[this._options.keys.faceUUID]
    }
    const merge = areProximate && isTheSamePerson
    // this._debugLog(
    //   detectionA,
    //   detectionB,
    //   distanceBetweenTimes,
    //   threshold,
    //   areProximate,
    //   isTheSamePerson,
    //   merge
    // )
    return merge
  }

  /**
   * Calculates and returnd the name of the camera, in a canonical format.
   *
   * @param {any} detection A detection object.
   * @returns A string, with non alphanumeric characters stripped.
   * @memberof DetectionMerger
   */
  cameraNameForDetection (detection) {
    const key = this._options.keys.cameraDescription
    if (!detection) {
      return undefined
    }
    const description = detection[key] || ''

    return description.replace(/\W/g, '').toLowerCase()
  }

  /**
   * @param {object} detection A detection to base comparison on.
   * @returns Either the most recent child of the detection that's passed in.
   * If there are no child detections, returns the detection itself.
   * @memberof DetectionMerger
   */
  detectionToCompareTo (detection) {
    if (detection.merged) {
      const last = detection.merged[detection.merged.length - 1]
      return last || detection
    }

    return detection
  }

  /**
   * Merges a parent detection into the histogram.
   *
   * @param {object} detection The detection to merge.
   * @memberof DetectionMerger
   */
  addParentDetectionToList (detection) {
    const cameraName = this.cameraNameForDetection(detection)
    this.mergedDetections.push(detection)
    this.currentByCamera[cameraName] = detection
  }

  /**
   * Add a detection to a detection as an associated detection.
   *
   * @param {any} child The detection to add as the child.
   * @param {any} parent The detection to merge the child to.
   * @memberof DetectionMerger
   */
  addChildDetectionToParent (child, parent) {
    if (!parent.merged) {
      parent.merged = [child]
      parent.mergeCount = 1
    } else {
      parent.merged.push(child)
      parent.mergeCount = (parent.mergeCount || 0) + 1
    }
  }

  _previousParent (detection) {
    const current = this.currentByCamera
    const cameraName = this.cameraNameForDetection(detection)
    const previousParent = current[cameraName]
    return previousParent
  }

  /**
   * Logs out some information about the detections being compared.
   *
   * @param {object} detectionA A detection object.
   * @param {object} detectionB A detection object.
   * @param {number} distanceBetweenTimes the number of milliseconds between detections.
   * @param {number} threshold The number of milliseconds defining proximity.
   * @param {boolean} areProximate Are the detections close to each other?
   * @param {boolean} isTheSamePerson Are both detections the same person?
   * @param {boolean} merge Should we merge?
   * @memberof DetectionMerger
   */
  _debugLog (
    detectionA,
    detectionB,
    distanceBetweenTimes,
    threshold,
    areProximate,
    isTheSamePerson,
    merge
  ) {
    // console.log(`Compare:\n${this.cameraNameForDetection(detectionA)}/${detectionA[this._options.keys.faceUUID]}/${detectionA[this._options.keys.timestamp]}\n${this.cameraNameForDetection(detectionB)}/${detectionB[this._options.keys.faceUUID]}/${detectionB[this._options.keys.timestamp]}\nDistance: ${distanceBetweenTimes}ms (${threshold}ms)\nIn Window? ${areProximate}\nSamePerson? ${isTheSamePerson}\nMerge? ${merge}`)
  }
}
